import { useEffect, useState } from 'react';
import { ApiError, completeFakePayment } from './api/client';
import type { FakeCompletionResponse } from './api/types';
import { clearPendingPurchase, readPendingPurchase } from './purchase/pendingPurchase';

function describeError(e: unknown): string {
  return e instanceof ApiError ? `${e.body.code}: ${e.body.description}` : 'Request failed';
}

/**
 * The checkout success-return route (/purchase/success). For a fake-provider purchase this page
 * *is* the provider redirect target, so it offers the webhook simulation the fake gateway needs;
 * for real providers it just confirms the round-trip back from the provider's hosted page.
 */
export function PurchaseSuccessPanel({ onBackToShop }: { onBackToShop: () => void }) {
  const [purchase] = useState(() => readPendingPurchase());
  const [completion, setCompletion] = useState<FakeCompletionResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // A non-fake return is terminal: the provider round-trip is over, nothing left to simulate.
  useEffect(() => {
    if (purchase !== null && purchase.provider !== 'fake') clearPendingPurchase();
  }, [purchase]);

  async function simulate(sessionId: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      setCompletion(await completeFakePayment(sessionId));
      clearPendingPurchase();
    } catch (e: unknown) {
      setError(describeError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      <h2>Back from checkout</h2>

      {error !== null && (
        <p role="alert" style={{ color: '#b00020' }}>
          {error}
        </p>
      )}

      {purchase === null ? (
        <p>
          No purchase is pending. This page is the checkout success-return target — start a
          purchase from the shop to land here with one.
        </p>
      ) : (
        <>
          <p>
            The <strong>{purchase.accessId}</strong> offering at <strong>{purchase.tenantId}</strong>{' '}
            (provider <strong>{purchase.provider}</strong>, session <code>{purchase.sessionId}</code>).
          </p>

          {purchase.provider === 'fake' ? (
            completion === null ? (
              <>
                <p>
                  The fake provider has no hosted checkout page — its redirect lands straight back
                  here. Completing the purchase means playing the provider: the button below posts
                  the fake gateway&rsquo;s signed webhook for this session to the payment host.
                </p>
                <button onClick={() => void simulate(purchase.sessionId)} disabled={busy}>
                  Simulate provider webhook
                </button>
              </>
            ) : (
              <>
                <p>
                  Webhook acknowledged: <code>received: {String(completion.received)}</code>.
                </p>
                <p>
                  The payment host verified the signature and recorded the completion in its logs —
                  no durable entitlement store exists yet, so the log line is the observable
                  outcome.
                </p>
              </>
            )
          ) : (
            <p>
              The provider redirected back here, so its hosted checkout finished. Completion itself
              arrives at the payment host on the provider&rsquo;s webhook, not through this page.
            </p>
          )}
        </>
      )}

      <p>
        <button onClick={onBackToShop}>Back to the shop</button>
      </p>
    </section>
  );
}
